'use client'
import {
  Typography_default
} from "./chunk-6UL5SZLI.mjs";
import {
  Button_default
} from "./chunk-ZVR4LKK3.mjs";
import {
  __spreadProps,
  __spreadValues
} from "./chunk-BTG5EALA.mjs";

// src/Modal/Modal.tsx
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { twMerge } from "tailwind-merge";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useDeviceDetect } from "@wbk/hooks";
import { jsx, jsxs } from "react/jsx-runtime";
var Modal = ({
  body,
  render,
  stickyHeader,
  stickyFooter,
  show,
  closeButton,
  onClose,
  size = "md",
  fullscreen,
  className,
  contentClassName,
  ignoreHistory,
  zIndex,
  title = "",
  description = "",
  unstyled,
  overlayClassName
}) => {
  const isClosing = useRef(false);
  const { isWebView } = useDeviceDetect();
  const [isOpen, setOpen] = useState(false);
  const externalState = typeof show !== "undefined";
  const close = () => {
    setOpen(false);
    if (onClose) {
      onClose();
    }
  };
  const open = () => {
    setOpen(true);
  };
  const maxWidthClass = useMemo(() => {
    switch (size) {
      case "xs":
        return "max-w-xs";
      case "sm":
        return "max-w-sm";
      case "lg":
        return "max-w-lg";
      case "xl":
        return "max-w-xl";
      case "2xl":
        return "max-w-2xl";
      case "4xl":
        return "max-w-4xl";
      case "7xl":
        return "max-w-7xl";
      default:
        return "max-w-md";
    }
  }, [size]);
  const back = () => {
    if (ignoreHistory) {
      return close();
    }
    if (isClosing.current)
      return;
    isClosing.current = true;
    history.back();
  };
  useEffect(() => {
    const on = externalState ? show : isOpen;
    if (on && !ignoreHistory) {
      history.pushState("", "");
      window.onpopstate = () => {
        close();
      };
      isClosing.current = false;
    }
  }, [show, isOpen, ignoreHistory]);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    render && render({ close: back, open, isOpen }),
    /* @__PURE__ */ jsx(
      DialogPrimitive.Root,
      {
        open: externalState ? show : isOpen,
        onOpenChange: (open2) => {
          if (!open2) {
            back();
          }
        },
        children: /* @__PURE__ */ jsxs(DialogPrimitive.Portal, { children: [
          /* @__PURE__ */ jsx(
            DialogPrimitive.Overlay,
            {
              style: { zIndex },
              className: twMerge(
                "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 bg-body/60 fixed inset-0 z-50",
                overlayClassName
              )
            }
          ),
          /* @__PURE__ */ jsx(
            DialogPrimitive.Content,
            {
              className: twMerge(
                unstyled ? "" : "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-left-1/2 data-[state=open]:slide-in-from-left-1/2 data-[state=closed]:slide-out-to-top-[45%] data-[state=open]:slide-in-from-top-[45%] fixed left-1/2 top-1/2 z-50 w-full -translate-x-1/2 -translate-y-1/2 outline-none",
                fullscreen ? "" : maxWidthClass,
                contentClassName
              ),
              style: { zIndex },
              children: /* @__PURE__ */ jsxs(
                "div",
                {
                  className: twMerge(
                    "bg-paper mini-scrollbar rounded-S border-paper relative w-full overflow-auto border",
                    fullscreen ? "h-screen" : "max-h-[85vh]",
                    className
                  ),
                  children: [
                    /* @__PURE__ */ jsx(VisuallyHidden, { asChild: true, children: /* @__PURE__ */ jsx(DialogPrimitive.Title, { children: title }) }),
                    /* @__PURE__ */ jsx(VisuallyHidden, { asChild: true, children: /* @__PURE__ */ jsx(DialogPrimitive.Description, { children: description }) }),
                    stickyHeader && /* @__PURE__ */ jsx("header", { className: "sticky top-0 z-10 shadow-sm", children: stickyHeader({ close: back }) }),
                    body({ close: back }),
                    stickyFooter && /* @__PURE__ */ jsx(
                      "footer",
                      {
                        className: `border-paper bg-paper rounded-b-S fixed bottom-0 left-0 w-full border-t shadow-sm ${isWebView ? "pb-8" : "safe-bottom"}`,
                        children: stickyFooter({ close: back })
                      }
                    ),
                    closeButton && /* @__PURE__ */ jsx(DialogPrimitive.Close, { asChild: true, children: /* @__PURE__ */ jsx(
                      Button_default,
                      {
                        className: "absolute top-4 z-20 p-px ltr:right-4 rtl:left-4",
                        theme: "transparent",
                        shape: "text",
                        "aria-label": "Close",
                        onClick: back,
                        children: /* @__PURE__ */ jsx("svg", { width: "20", height: "20", viewBox: "0 0 20 20", fill: "none", children: /* @__PURE__ */ jsx(
                          "path",
                          {
                            fillRule: "evenodd",
                            clipRule: "evenodd",
                            d: "M4.16679 2.98828L10.0001 8.82162L15.8335 2.98828L17.012 4.16679L11.1786 10.0001L17.012 15.8335L15.8335 17.012L10.0001 11.1786L4.16679 17.012L2.98828 15.8335L8.82162 10.0001L2.98828 4.16679L4.16679 2.98828Z",
                            fill: "currentColor"
                          }
                        ) })
                      }
                    ) })
                  ]
                }
              )
            }
          )
        ] })
      }
    )
  ] });
};
var Modal_default = Modal;

// src/Modal/Responsive.tsx
import { Fragment as Fragment2, useEffect as useEffect2, useImperativeHandle, useRef as useRef2, useState as useState2 } from "react";
import * as DialogPrimitive2 from "@radix-ui/react-dialog";
import { VisuallyHidden as VisuallyHidden2 } from "@radix-ui/react-visually-hidden";
import { Drawer } from "vaul";
import { twMerge as twMerge2 } from "tailwind-merge";
import { ChevronIcon, CloseIcon } from "@wbk/svg";
import { useTranslation } from "@wbk/config";

// src/Modal/Modal.classes.tsx
var WIDTH_CLASS = {
  xs: "max-w-full md:max-w-xs",
  sm: "max-w-full md:max-w-sm",
  md: "max-w-full md:max-w-md",
  lg: "max-w-full md:max-w-lg",
  xl: "max-w-full md:max-w-xl",
  "2xl": "max-w-full md:max-w-2xl",
  "4xl": "max-w-full md:max-w-[95%] lg:max-w-4xl",
  "7xl": "max-w-full md:max-w-[95%] 2xl:max-w-7xl"
};
var TYPE_CLASS = {
  "modal-sheet": `data-[state=open]:slide-in-from-bottom-full data-[state=closed]:slide-out-to-bottom-full
  md:data-[state=closed]:data-[position=center]:fade-out-0 md:data-[state=open]:data-[position=center]:fade-in-0
  md:data-[state=closed]:slide-out-to-left-1/2 md:data-[state=open]:slide-in-from-left-1/2
  md:bottom-auto bottom-0 md:data-[position=center]:left-1/2 md:data-[position=center]:top-1/2
  md:data-[position=center]:-translate-x-1/2 md:data-[position=center]:-translate-y-1/2

  md:data-[position=left]:slide-in-from-bottom-0 md:data-[position=left]:slide-out-to-bottom-0
  ltr:md:data-[position=left]:data-[state=closed]:slide-out-to-left-full ltr:md:data-[position=left]:data-[state=open]:slide-in-from-left-full
  ltr:md:data-[position=left]:left-0
  rtl:md:data-[position=left]:data-[state=closed]:slide-out-to-right-full rtl:md:data-[position=left]:data-[state=open]:slide-in-from-right-full
  rtl:md:data-[position=left]:right-0 data-[position=left]:md:h-full [&>div]:data-[position=left]:md:h-full

  md:data-[position=right]:slide-in-from-bottom-0 md:data-[position=right]:slide-out-to-bottom-0
  ltr:md:data-[position=right]:data-[state=closed]:slide-out-to-right-full ltr:md:data-[position=right]:data-[state=open]:slide-in-from-right-full
  ltr:md:data-[position=right]:right-0
  rtl:md:data-[position=right]:data-[state=closed]:slide-out-to-left-full rtl:md:data-[position=right]:data-[state=open]:slide-in-from-left-full
  rtl:md:data-[position=right]:left-0 data-[position=right]:md:h-full [&>div]:data-[position=right]:md:h-full

  md:data-[position=left]:top-0 md:data-[position=right]:top-0
  `,
  modal: `data-[state=open]:data-[position=center]:slide-in-from-bottom-full data-[state=closed]:data-[position=center]:slide-out-to-bottom-full
  data-[state=closed]:slide-out-to-left-1/2 data-[state=open]:slide-in-from-left-1/2
  data-[position=center]:left-1/2 data-[position=center]:top-1/2
  data-[position=center]:-translate-x-1/2 data-[position=center]:-translate-y-1/2

  ltr:data-[position=left]:data-[state=closed]:slide-out-to-left-full ltr:data-[position=left]:data-[state=open]:slide-in-from-left-full
  ltr:data-[position=left]:left-0
  rtl:data-[position=left]:data-[state=closed]:slide-out-to-right-full rtl:data-[position=left]:data-[state=open]:slide-in-from-right-full
  rtl:data-[position=left]:right-0
  data-[position=left]:h-full [&>div]:data-[position=left]:h-full

  ltr:data-[position=right]:data-[state=closed]:slide-out-to-right-full ltr:data-[position=right]:data-[state=open]:slide-in-from-right-full
  ltr:data-[position=right]:right-0
  rtl:data-[position=right]:data-[state=closed]:slide-out-to-left-full rtl:data-[position=right]:data-[state=open]:slide-in-from-left-full
  rtl:data-[position=right]:left-0
  data-[position=right]:h-full [&>div]:data-[position=right]:h-full

  data-[position=left]:top-0 data-[position=right]:top-0
  max-w-[95%] data-[fullscreen=true]:max-w-full [&>div]:data-[fullscreen=false]:data-[position=center]:max-h-[calc(100dvh-40px)]`,
  sheet: `data-[state=open]:slide-in-from-bottom-full data-[state=closed]:slide-out-to-bottom-full
  bottom-0 !max-w-full`
};
var ROUNDNESS_CLASS = {
  "modal-sheet": `rounded-t-S md:rounded-S
  ltr:[&>div]:data-[position=left]:md:rounded-l-none
  rtl:[&>div]:data-[position=left]:md:rounded-r-none
  ltr:[&>div]:data-[position=right]:md:rounded-r-none
  rtl:[&>div]:data-[position=right]:md:rounded-l-none
  `,
  modal: `rounded-S

  ltr:[&>div]:data-[position=left]:rounded-l-none
  rtl:[&>div]:data-[position=left]:rounded-r-none

  ltr:[&>div]:data-[position=right]:rounded-r-none
  rtl:[&>div]:data-[position=right]:rounded-l-none`,
  sheet: "rounded-t-S"
};
var SURFACE_CLASSES = {
  default: "bg-paper",
  "level-1": "bg-paper-level-1",
  "level-2": "bg-paper-level-2",
  "level-3": "bg-paper-level-3"
};
var BORDER_CLASSES = {
  default: "border border-paper",
  "level-1": "border border-paper-level-1",
  "level-2": "border border-paper-level-2",
  "level-3": "border border-paper-level-3"
};

// src/Modal/Responsive.tsx
import { Fragment as Fragment3, jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var ResponsiveModal = ({
  render,
  body,
  closeButton,
  dismissible = true,
  onClose,
  ignoreHistory,
  fullscreen,
  size = "md",
  type = "modal-sheet",
  position = "center",
  title,
  className,
  bodyClassName,
  actionsStyle = "responsive",
  action,
  cancel,
  previous,
  customAction,
  ref,
  surface = "level-1",
  border = true
}) => {
  const { t } = useTranslation("ui_common");
  const isClosing = useRef2(false);
  const [isOpen, setOpen] = useState2(false);
  const isMd = typeof window !== "undefined" && window.innerWidth < 768;
  const useVaul = type === "sheet" || isMd && type !== "modal";
  const Dialog = useVaul ? Drawer : DialogPrimitive2;
  const hasCancal = cancel !== false;
  const hasAction = action !== false;
  const hasCloseButton = closeButton !== false;
  const hasFooter = hasCancal || hasAction || !!previous || customAction;
  useImperativeHandle(ref, () => ({
    open,
    close
  }));
  const close = () => {
    setOpen(false);
    if (onClose) {
      onClose();
    }
  };
  const open = () => {
    setOpen(true);
  };
  const back = () => {
    if (ignoreHistory) {
      return close();
    }
    if (isClosing.current)
      return;
    isClosing.current = true;
    history.back();
  };
  useEffect2(() => {
    if (isOpen && !ignoreHistory) {
      history.pushState(null, "");
      window.onpopstate = () => {
        close();
      };
      isClosing.current = false;
    }
  }, [isOpen, ignoreHistory]);
  return /* @__PURE__ */ jsxs2(Fragment2, { children: [
    render && render({ close: back, open, isOpen }),
    /* @__PURE__ */ jsx2(
      Dialog.Root,
      {
        open: isOpen,
        onOpenChange: (open2) => {
          if (dismissible && !open2) {
            back();
          }
        },
        dismissible,
        repositionInputs: false,
        children: /* @__PURE__ */ jsxs2(Dialog.Portal, { children: [
          /* @__PURE__ */ jsx2(Dialog.Overlay, { className: "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 bg-body-dark/60 fixed inset-0 z-[998]" }),
          /* @__PURE__ */ jsx2(
            Dialog.Content,
            {
              "data-fullscreen": !!fullscreen,
              "data-position": position,
              "aria-describedby": typeof title === "string" ? title : void 0,
              className: twMerge2(
                "fixed z-[999] w-full outline-none",
                "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-300 data-[state=closed]:data-[position=center]:md:duration-100 data-[state=open]:data-[position=center]:md:duration-100",
                fullscreen ? "" : WIDTH_CLASS[size],
                TYPE_CLASS[type],
                className
              ),
              children: /* @__PURE__ */ jsxs2(
                "div",
                {
                  className: twMerge2(
                    "relative flex max-h-dvh w-full flex-col overflow-hidden",
                    ROUNDNESS_CLASS[type],
                    border && BORDER_CLASSES[surface],
                    SURFACE_CLASSES[surface],
                    fullscreen ? "h-dvh" : "data-[center=true]:md:max-h-[calc(100dvh-40px)]"
                  ),
                  "data-fullscreen": fullscreen,
                  "data-center": position === "center",
                  children: [
                    /* @__PURE__ */ jsx2(
                      "div",
                      {
                        className: twMerge2(
                          "mini-scrollbar grow overflow-hidden overflow-y-auto p-6 pt-2",
                          bodyClassName
                        ),
                        children: body({ close: back })
                      }
                    ),
                    hasFooter && /* @__PURE__ */ jsx2(
                      "div",
                      {
                        className: twMerge2(
                          "border-paper flex justify-between gap-4 border-t px-6",
                          useVaul ? "safe-bottom pt-4" : "py-4"
                        ),
                        children: customAction ? customAction({ close: back }) : /* @__PURE__ */ jsxs2(Fragment3, { children: [
                          previous ? /* @__PURE__ */ jsx2(
                            Button_default,
                            __spreadProps(__spreadValues({
                              type: "button",
                              shape: "outlined",
                              theme: "action"
                            }, previous), {
                              className: twMerge2("ps-3", previous == null ? void 0 : previous.className),
                              children: /* @__PURE__ */ jsxs2(Typography_default, { className: "flex items-center gap-1", children: [
                                /* @__PURE__ */ jsx2(ChevronIcon, { className: "h-3 w-3 rotate-180" }),
                                (previous == null ? void 0 : previous.label) || t("ui_common:back")
                              ] })
                            })
                          ) : null,
                          /* @__PURE__ */ jsxs2(
                            "div",
                            {
                              "data-flat": !!previous || !hasCancal,
                              "data-responsive": actionsStyle === "responsive",
                              className: "data-[flat=false]:data-[responsive]:xs:flex-row flex grow justify-end gap-2 data-[flat=false]:data-[responsive=true]:flex-col data-[flat=false]:data-[responsive=false]:justify-between data-[flat]:data-[responsive]:md:justify-end",
                              children: [
                                hasCancal && /* @__PURE__ */ jsx2(
                                  Button_default,
                                  __spreadProps(__spreadValues({
                                    type: "button",
                                    theme: "action",
                                    shape: "text"
                                  }, cancel), {
                                    onClick: () => {
                                      if (cancel == null ? void 0 : cancel.onClick) {
                                        return cancel.onClick({ close: back });
                                      }
                                      back();
                                    },
                                    children: /* @__PURE__ */ jsx2(Typography_default, { children: (cancel == null ? void 0 : cancel.label) || t("ui_common:cancel") })
                                  })
                                ),
                                hasAction && /* @__PURE__ */ jsx2(
                                  Button_default,
                                  __spreadProps(__spreadValues({
                                    type: "submit",
                                    theme: "action"
                                  }, action), {
                                    onClick: () => {
                                      if (action == null ? void 0 : action.onClick) {
                                        return action.onClick({ close: back });
                                      }
                                    },
                                    children: /* @__PURE__ */ jsx2(Typography_default, { strong: true, children: (action == null ? void 0 : action.label) || t("ui_common:confirm") })
                                  })
                                )
                              ]
                            }
                          )
                        ] })
                      }
                    ),
                    /* @__PURE__ */ jsxs2(
                      "div",
                      {
                        "data-invisible": !title && !hasCloseButton,
                        className: twMerge2(
                          "sticky top-0 z-10 -order-1 data-[invisible=true]:hidden",
                          SURFACE_CLASSES[surface]
                        ),
                        style: { transform: "translateZ(0)" },
                        children: [
                          /* @__PURE__ */ jsx2(
                            "div",
                            {
                              "data-invisible": type === "modal",
                              "data-md-visible": type === "sheet",
                              className: "after:bg-paper-level-1 relative flex h-8 shrink-0 items-center justify-center after:h-[5px] after:w-9 after:rounded-full data-[invisible=true]:invisible md:hidden data-[md-visible=true]:md:flex"
                            }
                          ),
                          /* @__PURE__ */ jsx2(Dialog.Title, { className: "px-6 pb-2 md:py-4", children: /* @__PURE__ */ jsx2(Typography_default, { as: "div", variant: "heading-L", children: title }) }),
                          /* @__PURE__ */ jsx2(VisuallyHidden2, { children: /* @__PURE__ */ jsx2(Dialog.Description, { asChild: true, children: title }) }),
                          hasCloseButton && /* @__PURE__ */ jsx2(Dialog.Close, { asChild: true, children: /* @__PURE__ */ jsx2(
                            Button_default,
                            __spreadProps(__spreadValues({
                              className: "absolute top-2.5 z-20 p-px ltr:right-3 rtl:left-3",
                              theme: "transparent",
                              shape: "text",
                              "aria-label": "Close"
                            }, closeButton), {
                              onClick: back,
                              children: /* @__PURE__ */ jsx2(CloseIcon, { className: "h-5 w-5" })
                            })
                          ) })
                        ]
                      }
                    )
                  ]
                }
              )
            }
          )
        ] })
      }
    )
  ] });
};
var Responsive_default = ResponsiveModal;

export {
  Modal_default,
  Responsive_default
};
//# sourceMappingURL=chunk-4F2BFOUJ.mjs.map